// Optional per-tile elevation layer for the map — a feature-flagged extension.
// Additive & inert: chunks MAY carry an `elev` array of integer height tiers
// (sea level = 0), parallel to `tiles`. Adjacent hexes whose tiers differ by more
// than one form an impassable cliff (crossable only by a ramp). None of this takes
// effect unless the server ENABLE_ELEV flag populates chunk.elev at worldgen; with
// the flag off `elev` is undefined everywhere and these helpers are never consulted.
// Shared. Additive to the frozen contract — see the flat-world default in tierOf().

import { DIRS } from './hex'

/** Lowest / highest height tier a tile may have. Sea level is tier 0. */
export const ELEV_MIN = -10
export const ELEV_MAX = 10

/** Tiers per unit of elevation noise above sea level (worldgen quantization). */
export const ELEV_SCALE = 12

/** Greatest tier difference between adjacent hexes a unit can still walk;
 *  anything steeper is a cliff (passable only via a ramp edge). */
export const ELEV_MAX_STEP = 1

/** Quantize a worldgen elevation-noise sample (relative to sea level) to a tier. */
export function elevTier(e: number, sea: number): number {
  const t = Math.round((e - sea) * ELEV_SCALE)
  return t < ELEV_MIN ? ELEV_MIN : t > ELEV_MAX ? ELEV_MAX : t
}

/** Height tier of local tile `idx` within a chunk, or 0 when the chunk carries
 *  no elevation layer (flat / legacy world). */
export function tierOf(elev: number[] | undefined, idx: number): number {
  return elev ? (elev[idx] ?? 0) : 0
}

/** Is the step between two tiers too steep to walk on foot (a cliff face)? */
export function tooSteep(tierA: number, tierB: number): boolean {
  return Math.abs(tierA - tierB) > ELEV_MAX_STEP
}

/** The height tier a multi-hex site's crew levels its footprint TO: the majority
 *  tier of the footprint, ties breaking toward the anchor (foot[0]) so the clicked
 *  hex keeps its ground when it can. Deterministic from current tiers only, so the
 *  server (heightTierAt) and client (elevAt) always agree — and because the crew only
 *  ever moves hexes TOWARD this tier, recomputing mid-prep returns the same target.
 *  Undefined when there is no elevation data (flat/legacy world → nothing to level). */
export function levelTarget(
  foot: { q: number; r: number }[],
  tierAt: (q: number, r: number) => number | undefined,
): number | undefined {
  const anchor = tierAt(foot[0].q, foot[0].r)
  if (anchor === undefined) return undefined
  const counts = new Map<number, number>()
  for (const c of foot) {
    const t = tierAt(c.q, c.r)
    if (t !== undefined) counts.set(t, (counts.get(t) ?? 0) + 1)
  }
  let maj = anchor
  let majN = -1
  for (const [t, n] of counts) if (n > majN || (n === majN && t === anchor)) { maj = t; majN = n }
  return maj
}

/** The town hall is a built platform: it may only stand where its whole footprint
 *  sits at ONE height tier and every hex ringing that footprint is within
 *  ELEV_MAX_STEP of it — so a capital never straddles a cliff (which read as broken
 *  on the 2.5D slope). `foot` is the footprint hexes (anchor first); `tierAt` returns
 *  a hex's tier, or undefined when the hex is unknown (client fog / off-map).
 *  Returns null when the ground is fine, else a short player-facing reason. Degrades
 *  to "always fine" on flat worlds: the anchor tier is undefined (client elevAt) or
 *  every tier is 0 (server heightTierAt), so the checks trivially pass.
 *  `requireFlat` — true for FOUNDING (findPlayerSpawn: no serfs exist yet to level a
 *  site, so the platform must already be flat); false for player placement, where the
 *  crew levels the footprint to levelTarget() first — only the RIM rule remains (judged
 *  against that target), so the finished capital still never overlooks a cliff. */
export function townhallGroundReason(
  foot: { q: number; r: number }[],
  tierAt: (q: number, r: number) => number | undefined,
  requireFlat = true,
): string | null {
  const base = requireFlat ? tierAt(foot[0].q, foot[0].r) : levelTarget(foot, tierAt)
  if (base === undefined) return null // no elevation data → no platform constraint
  const inFoot = new Set(foot.map((c) => c.q + ',' + c.r))
  if (requireFlat) {
    for (const c of foot) {
      if (tierAt(c.q, c.r) !== base) return 'The town hall needs seven hexes at one height'
    }
  }
  for (const c of foot) {
    for (const d of DIRS) {
      const nq = c.q + d.q
      const nr = c.r + d.r
      if (inFoot.has(nq + ',' + nr)) continue
      const t = tierAt(nq, nr)
      if (t !== undefined && tooSteep(t, base)) return 'The ground around the town hall is too steep'
    }
  }
  return null
}

export interface TownhallHint {
  /** Per-footprint-hex, aligned with `foot`: true if the hex sits at the MAJORITY
   *  height the hall would level to (paint green), false if it's a minority outlier
   *  (paint red). */
  cellOk: boolean[]
  /** Ring hexes more than ELEV_MAX_STEP off that majority height — also blockers,
   *  painted red outside the footprint. */
  steepRing: { q: number; r: number }[]
}

/** Colour hint for the town-hall build ghost. The crew levels the footprint to its
 *  MAJORITY height (levelTarget), so off-height cells are WORK, not blockers — every
 *  footprint cell reads green. Only a too-steep rim hex (vs that target) still blocks,
 *  painted red outside the footprint. Returns null on flat/no-elevation worlds. */
export function townhallHexHint(
  foot: { q: number; r: number }[],
  tierAt: (q: number, r: number) => number | undefined,
): TownhallHint | null {
  const maj = levelTarget(foot, tierAt)
  if (maj === undefined) return null
  const cellOk = foot.map(() => true)
  const inFoot = new Set(foot.map((c) => c.q + ',' + c.r))
  const steepRing: { q: number; r: number }[] = []
  const seen = new Set<string>()
  for (const c of foot) {
    for (const d of DIRS) {
      const nq = c.q + d.q
      const nr = c.r + d.r
      const k = nq + ',' + nr
      if (inFoot.has(k) || seen.has(k)) continue
      const t = tierAt(nq, nr)
      if (t !== undefined && tooSteep(t, maj)) { seen.add(k); steepRing.push({ q: nq, r: nr }) }
    }
  }
  return { cellOk, steepRing }
}
